// Centralised list of feature-flag keys for the web admin/client portal.
//
// To add a flag: add an entry to FLAGS, give it a default in DEFAULTS, then
// publish the same key in the Firebase console (Remote Config) on the
// `moblyze-admin-portal` project with the production value (and any
// role/agency conditions). Until the console value lands every session sees
// the DEFAULT, so a flag never silently flips for users.
//
// Targeting on user properties (role / agency_slug / user_id) is done with Remote
// Config *custom signals* — see `setFeatureFlagUserContext` in
// featureFlags.js. Owner users are reported as role "Admin"; there is no
// separate owner signal. The conditions themselves live in the Firebase
// console, mirroring the mobile app's "conditions live in the console" model.

export const FLAGS = {
  WEB_EXAMPLE_FLAG: "web_example_flag",

  // External-candidate rollout. External (pending sign-up) candidates can be
  // viewed/favourited but, until each flag flips on, cannot be invited to a
  // job or moved to a shortlist — the API rejects those actions while the
  // flags are off, so the admin UI hides them too. Flip the matching flag on
  // in the Firebase console (optionally targeted by role/agency) to re-enable.
  EXTERNAL_CANDIDATE_JOB_INVITE: "external_candidate_job_invite",
  EXTERNAL_CANDIDATE_SHORTLIST: "external_candidate_shortlist",
  WEB_JOB_CREATE_V2: "web_job_create_v2",
  WEB_JOB_BULLHORN_PREFILL: "web_job_bullhorn_prefill",

  // Rebuilt tables (shadcn/ui data-table): user-controlled column visibility
  // plus responsive collapsing for narrow screens and the Bullhorn iframe. Off
  // until the console value lands, so users keep the existing tables.
  WEB_RESPONSIVE_TABLES: "web_responsive_tables",

  // The Moblyze tab in Bullhorn moves to the clients app. On, the embed page
  // (/agency/external/bullhorn) sends the frame on to clients.moblyze.me/bullhorn
  // with Bullhorn's query string. Off, it keeps serving the tab here.
  WEB_BULLHORN_TAB_TO_CLIENTS: "web_bullhorn_tab_to_clients",
};

function envBool(name) {
  return process.env[name] === "true";
}

// FlagValue is one of: boolean | string | number (the three Remote Config
// types). Defaults must cover every key in FLAGS.
export const DEFAULTS = {
  [FLAGS.WEB_EXAMPLE_FLAG]: false,
  
  // Off by default: external candidates have invite/shortlist hidden until the
  // console value lands, so the action never silently appears before the API
  // is ready for it.
  [FLAGS.EXTERNAL_CANDIDATE_JOB_INVITE]: false,
  [FLAGS.EXTERNAL_CANDIDATE_SHORTLIST]: false,
  
  // Local .env values seed defaults when Remote Config is unavailable (e.g.
  // dev without Firebase credentials). Production targeting lives in Firebase.
  [FLAGS.WEB_JOB_CREATE_V2]: envBool("REACT_APP_JOB_CREATE_V2"),
  [FLAGS.WEB_JOB_BULLHORN_PREFILL]: envBool("REACT_APP_JOB_BULLHORN_PREFILL"),
  [FLAGS.WEB_RESPONSIVE_TABLES]: envBool("REACT_APP_RESPONSIVE_TABLES"),
  [FLAGS.WEB_BULLHORN_TAB_TO_CLIENTS]: false,
};

// Enumerated allowed values for a string/number flag. When a flag is listed
// here the dev override panel renders a picker instead of a free-text/toggle
// input. Listing a key is purely a UX affordance — it does not validate the
// runtime value. (Empty for now; the example flag is a plain boolean.)
export const ALLOWED_VALUES = {};
